import './ServiceEdit.css';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from "react-helmet";
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Services } from '../../api/Services';
import AppLoader from '../../components/common/AppLoader';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { BdaService, BdaServiceProperty, Framework } from '../../models/BdaService';
import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Button, Card, Col, Container, Form, Row, Breadcrumb, Table } from 'react-bootstrap';
import { Frameworks } from '../../api/Frameworks';
import ScriptEditor from '../../components/common/ScriptEditor';
import { cloneDeep, debounce } from 'lodash';
import { FrameworkOption, getPropertyIcon } from '../../utils';
import IconButton from '../../components/common/IconButton';
import { Flex, Modal, Select } from 'antd';
import { editor } from 'monaco-editor';
import { Option } from '../../models/generics';
import MDEditor from '@uiw/react-md-editor';
import rehypeSanitize from "rehype-sanitize";
import ServicePropertyModal from '../../components/generics/ServicePropertyModal';
import { v4 } from 'uuid';


const SERVICE_NAME_REGEX = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;

interface ValidationError {
  key: string;
  message: string;
}

export default function ServiceEdit() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [cloneMode, setCloneMode] = useState<boolean>();
  const navigate = useNavigate();

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const [validated, setValidated] = useState(false);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showDefaultValueModal, setShowDefaultValueModal] = useState(false);
  const [showDefinitionModal, setShowDefinitionModal] = useState(false);

  const [service, setService] = useState<BdaService>();
  const [frameworks, setFrameworks] = useState<Framework[]>();
  const [selectedFramework, setSelectedFramework] = useState<number>();
  const [frameworkOptions, setFrameworkOptions] = useState<FrameworkOption[]>();
  const [newProperty, setNewProperty] = useState(new BdaServiceProperty('application'));
  const [editing, setEditing] = useState<number>();

  const [areasOptions, setAreasOptions] = useState<Option[]>();
  const [acceptedAreas, setAcceptedAreas] = useState<string[]>();
  const [acceptedModes, setAcceptedModes] = useState<string[]>();

  const [nameError, setNameError] = useState(false);
  const [definitionErrors, setDefinitionErrors] = useState<editor.IMarker[]>([]);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [serviceJSON, setServiceJSON] = useState<string>();
  const [defaultValue, setDefaultValue] = useState("");

  const disableNonPatchFields = useMemo(() => id !== undefined && !cloneMode, [id, cloneMode]);

  useEffect(() => {
    if (id) retrieveService();
    else setService(new BdaService());

    initFrameworks();
    initModes();
    initAreas();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (service) {
      validateInformation(service);
      const definition = JSON.stringify(service, (_, value) => typeof value === 'undefined' ? null : value, "\t");
      setServiceJSON(definition);
    }
    // eslint-disable-next-line
  }, [service]);

  const retrieveService = () => {
    Services.getService(+id!)
      .then(
        ({ data }) => {
          const mode = searchParams.get("mode");
          setCloneMode(mode === "clone");

          Services.getServiceMarkDown(+id!)
            .then(
              ({ data: markdown }) => setService({ ...data, markDown: { value: markdown } }),
              (err) => {
                const message = err.response?.data?.message || err.message;
                const status = err.response?.status;
                showToast('service-md-error', message, `Service Markdown Error ${status ? `(${status})` : ''}`, 'danger');
                setService(data);
              }
            );
          setSelectedFramework(data.framework?.id);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('service-error', message, `Service Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  // TODO remove this
  const initModes = () => {
    Services.getModes()
      .then(
        ({ data: modes }) => setAcceptedModes(modes),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('modes-error', message, `Modes Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const initAreas = () => {
    Services.getAreas()
      .then(
        ({ data: areas }) => {
          setAcceptedAreas(areas);
          setAreasOptions(
            areas.map(area => ({
              label: area.split("_").join(" "),
              value: area,
            }))
          )
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('areas-error', message, `Areas Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const initFrameworks = () => {
    Frameworks.getFrameworks()
      .then(
        ({ data: frameworks }) => {
          setFrameworks(frameworks);
          setFrameworkOptions(
            frameworks.map(f => ({
              label: f.name,
              value: f.id,
              image: f.imageUrl,
              trailing: f.version,
            }))
          );
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('frameworks-error', message, `Frameworks Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const openDefinitionModal = () => setShowDefinitionModal(true);
  const handlePropertyModalClose = () => setShowPropertyModal(false);
  const handleDefinitionModalClose = () => setShowDefinitionModal(false);
  const handleAddProperty = () => {
    setNewProperty(new BdaServiceProperty('application'));
    setShowPropertyModal(true);
  }

  const handleSubmitProperty = (values: any) => {
    setService(curr => {
      const newProperties = cloneDeep(curr!.properties);
      if (editing !== undefined) newProperties[editing] = values;
      else newProperties.push(values);
      return { ...curr!, properties: newProperties };
    });
    setNewProperty(new BdaServiceProperty('application'));
    setShowPropertyModal(false);
  }

  const cancel = () => navigate('/services');

  useEffect(() => {
    setService((previous) => ({
      ...previous!,
      framework: frameworks?.find((framework) => framework.id === selectedFramework),
    }))
  }, [selectedFramework, frameworks]);

  const handleFieldChange = (field: keyof BdaService) => (evt: any) => {
    const value = (field === 'area' || field === 'framework' || field === 'tags') ? evt : evt.target.value;
    if (field === "framework") setSelectedFramework(value);
    else {
      setService({
        ...service!,
        [field]: value,
      });
    }
    if (field === "name") {
      if (!SERVICE_NAME_REGEX.test(value) || value.length > 30) setNameError(true);
      else setNameError(false);
    }
  };

  const sendRequest = useCallback(() => {
    showLoader();

    const body = { ...service! };
    if (!body.mode || acceptedModes!.includes(body.mode)) body.mode = 'BATCH';

    const promise = id
      ? cloneMode
        ? Services.updateService(body!)
        : Services.updateServiceFields(body!)
      : Services.createService(body!);

    promise
      .then(
        ({ data }) => {
          hideLoader();
          showToast('services-success', `Service ${data.name} ${id ? 'updated' : 'created'} successfully.`, 'Success', 'success');
          navigate(`/services/${data.id}`);
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('services-error', message, `Could not ${id ? 'update' : 'create'} service${status ? ` (${status})` : ''}`, 'danger');
        }
      );
  }, [service, acceptedModes, showLoader, hideLoader, showToast, id, cloneMode, navigate]);

  const submitService = useCallback((evt: React.FormEvent<HTMLFormElement>) => {
    const form = evt.currentTarget;
    evt.preventDefault();
    evt.stopPropagation();

    setValidated(true);

    if (!service!.framework || !service!.area) return;
    if (form.checkValidity() === true) sendRequest();
  }, [service, sendRequest]);

  const submitDefinition = () => {
    const newService = JSON.parse(serviceJSON!);
    if (!newService.properties) newService.properties = [];
    setService(newService);
    handleDefinitionModalClose();
  }

  const editProperty = (index: number) => () => {
    setEditing(index);
    setNewProperty({ ...service!.properties[index] });
    setShowPropertyModal(true);
  }

  const cloneProperty = (index: number) => () => {
    const cloned = cloneDeep(service!.properties[index]);
    cloned.key += `-${v4().substring(0, 6)}`;
    setService({
      ...service!,
      properties: service!.properties.concat(cloned),
    });
  }

  const deleteProperty = (index: number) => () => {
    const newProperties = [...service!.properties];
    newProperties.splice(index, 1);
    setService({
      ...service!,
      properties: newProperties
    });
  }

  const handleDefaultValueModalClose = useCallback(() => {
    setDefaultValue("");
    setShowDefaultValueModal(false);
  }, []);
  const openDefaultValueModal = useCallback((property: BdaServiceProperty) => () => {
    setDefaultValue(property.defaultValue);
    setShowDefaultValueModal(true);
  }, []);

  const renderServiceProperties = () => {
    if (!service!.properties.length) return (
      <Alert className="py-2" variant="primary">
        <FontAwesomeIcon icon={solid('info-circle')} />
        <span className="ms-2">No properties found.</span>
      </Alert>
    )

    return (
      <Table className="mb-0">
        <thead>
          <tr>
            <th style={{ width: 20 }}></th>
            <th style={{ width: '25%' }}>Key</th>
            <th className="text-center">Type</th>
            <th className="text-center">Default Value</th>
            <th style={{ width: 20 }} className="text-center">Mandatory</th>
            {!disableNonPatchFields && <th className="text-center">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {service!.properties.map((property, index) => (
            <tr key={`prop-${property.key}`}>
              <td>
                <FontAwesomeIcon icon={getPropertyIcon(property.type)} />
              </td>
              <td>{property.key}</td>
              <td className="text-center">{property.valueType}</td>
              <td className="text-center">
                {property.valueType === 'JSON'
                  ? (
                    <Button size="sm" variant="light" className="border" onClick={openDefaultValueModal(property)}>
                      <FontAwesomeIcon icon={solid('eye')} />
                      <span className="ms-2">Show Value</span>
                    </Button>
                  )
                  : property.defaultValue
                }
              </td>
              <td className="text-center">{property.mandatory && <FontAwesomeIcon icon={solid('check-square')} />}</td>
              {!disableNonPatchFields && (
                <td className="text-center">
                  <IconButton type="button" className="ms-2" onClick={cloneProperty(index)} tooltip="Clone">
                    <FontAwesomeIcon icon={regular('clone')} />
                  </IconButton>
                  <IconButton type="button" onClick={editProperty(index)} tooltip="Edit">
                    <FontAwesomeIcon icon={solid('pen')} />
                  </IconButton>
                  <IconButton type="button" className="text-danger ms-2" onClick={deleteProperty(index)} tooltip="Remove">
                    <FontAwesomeIcon icon={solid('times')} />
                  </IconButton>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    )
  }

  const handleMarkdownChange = useCallback((value?: string) => {
    setService(previous => ({
      ...previous!,
      markDown: {
        value: value || "",
      }
    }))
  }, []);

  const renderServiceForm = () => {
    return (
      <Form noValidate validated={validated} onSubmit={submitService}>
        <Card.Body className="service-form-wrapper">
          <Row>
            <Col lg={6}>
              <Row className="gx-2 mb-3">
                <Col>
                  <Form.Group controlId="service-name" className="mb-3">
                    <Form.Label>Name</Form.Label>
                    <Form.Control size="sm" placeholder="Service Name *" value={service!.name} onChange={handleFieldChange('name')} required />
                    <Form.Control.Feedback type="invalid">Service name is required.</Form.Control.Feedback>
                    {nameError && <Form.Control.Feedback type="invalid" className="d-block">Service name can include just numbers, letters and dash ('-') and must be under 30 characters.</Form.Control.Feedback>}
                  </Form.Group>

                  <Form.Group controlId="service-description">
                    <Form.Label>Description</Form.Label>
                    <Form.Control as="textarea" rows={4} placeholder="Service description" value={service!.description} onChange={handleFieldChange('description')}></Form.Control>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group controlId="service-version" className="mb-3">
                    <Form.Label>Version</Form.Label>
                    <Form.Control size="sm" placeholder="Service Version *" value={service!.version} onChange={handleFieldChange('version')} required disabled={disableNonPatchFields} />
                    <Form.Control.Feedback type="invalid">Service Version is required.</Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Framework</Form.Label>
                    <Select
                      allowClear
                      className="w-100"
                      placeholder="Select service framework"
                      loading={!frameworkOptions}
                      value={selectedFramework}
                      onChange={handleFieldChange('framework')}
                      disabled={disableNonPatchFields}
                    >
                      {frameworkOptions?.map(option => (
                        <Select.Option key={option.value} value={option.value} label={option.label}>
                          <Flex align="center">
                            <div className="framework-image me-2" style={{ backgroundImage: `url(${option.image})` }}></div>
                            <h6 className="mb-0">{option.label}</h6>
                            <span className="text-muted ms-3">{option.trailing}</span>
                          </Flex>
                        </Select.Option>
                      ))}
                    </Select>
                    {validated && !service!.framework && <Form.Control.Feedback className="d-block" type="invalid">Service framework is required.</Form.Control.Feedback>}
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>Area</Form.Label>
                    <Select
                      allowClear
                      className="w-100"
                      placeholder="Select service area"
                      options={areasOptions}
                      value={service!.area}
                      onChange={handleFieldChange('area')}
                      disabled={disableNonPatchFields}
                    />
                    {validated && !service!.area && <Form.Control.Feedback className="d-block" type="invalid">Service area is required.</Form.Control.Feedback>}
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group controlId="service-url" className="mb-3">
                <Form.Label>Url</Form.Label>
                <Form.Control size="sm" placeholder="Service URL *" type="url" value={service!.url} onChange={handleFieldChange('url')} required disabled={disableNonPatchFields} />
                <Form.Control.Feedback type="invalid">Service Url is required.</Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId="service-tags" className="mb-3">
                <Form.Label>Tags</Form.Label>
                <Select mode="tags" className="w-100" placeholder="Service Tags" value={service!.tags} onChange={handleFieldChange('tags')} />
              </Form.Group>

              <Flex align="center" justify="space-between" className="mb-2">
                <h6 className="mb-0">Service Properties</h6>

                {!disableNonPatchFields && (
                  <Button size="sm" variant="light" className="border" onClick={handleAddProperty}>
                    <FontAwesomeIcon icon={solid('plus')} />
                    <span className="ms-2">Add property</span>
                  </Button>
                )}
              </Flex>
              {service!.properties && renderServiceProperties()}
            </Col>

            <Col lg={6} className="mt-3 mt-lg-0">
              <Flex vertical className="h-100">
                <h6 className="d-lg-none">Service Markdown</h6>
                <Col data-color-mode="light">
                  <MDEditor
                    value={service!.markDown?.value || ""}
                    onChange={handleMarkdownChange}
                    previewOptions={{
                      rehypePlugins: [[rehypeSanitize]],
                    }}
                    textareaProps={{ placeholder: "Enter service markdown here." }}
                    height="100%"
                  />
                </Col>
              </Flex>
            </Col>
          </Row>
        </Card.Body>

        <Flex align="center" justify="space-between" className="border-top p-2 px-3">
          <Button type="button" variant="light" className="border" onClick={cancel}>Cancel</Button>
          <Button type="submit" variant="success">
            <FontAwesomeIcon icon={solid('save')} />
            <span className="ms-2">Save</span>
          </Button>
        </Flex>
      </Form>
    )
  }

  const validateInformation = function (service: BdaService) {
    console.log("validating", service);

    const errors = [];
    if (!service.name || service.name === "") errors.push({ key: 'name', message: 'is required and cannot be empty' });
    else if (!SERVICE_NAME_REGEX.test(service.name) || service.name.length > 30) errors.push({ key: 'name', message: "can include just numbers, letters and dash ('-') and must be under 30 characters." });
    if (!service.area || service.area === "") errors.push({ key: 'area', message: 'is required and cannot be empty' });
    else if (acceptedAreas && !acceptedAreas.includes(service.area)) errors.push({ key: 'area', message: `must be one of the following values: ${acceptedAreas.join(', ')}` })
    if (!service.version || service.version === "") errors.push({ key: 'version', message: 'is required and cannot be empty' });
    if (!service.framework) errors.push({ key: 'framework', message: 'is required and cannot be empty' });
    if (!service.url || service.url === "") errors.push({ key: 'url', message: 'is required and cannot be empty' });
    setErrors(errors);
  }

  // eslint-disable-next-line
  const debounceParsing = useCallback(
    debounce(function (value) {
      try {
        const newService: BdaService = JSON.parse(value);
        // TODO should we improve this?
        if (!id) {
          delete newService.id;
          delete newService.ref;
          delete newService.label;
          delete newService.statusModel;
          delete newService.models;
          delete newService.organization;
          delete newService.owner;
          newService.properties?.forEach(prop => delete prop.id);
        }
        const newDefinition = JSON.stringify(newService, (_, value) => typeof value === 'undefined' ? null : value, "\t");
        setServiceJSON(newDefinition);
        validateInformation(newService);
      } catch (e) {
        console.log('JSON is not valid', e);
      }
    }, 1000),
    []
  );

  const handleJSONErrors = (errors: editor.IMarker[]) => setDefinitionErrors(errors || [])

  const renderServiceJSON = () => {
    return (
      <Row className="gx-2 align-items-start">
        <Col md={8}>
          <ScriptEditor
            value={serviceJSON}
            onChange={debounceParsing}
            onValidated={handleJSONErrors}
          />
        </Col>

        <Col md={4}>
          {definitionErrors?.map(({ message, startLineNumber, startColumn, endLineNumber, endColumn }, index) => (
            <Alert className="py-2 mb-2" key={index} variant="danger">
              <FontAwesomeIcon icon={solid('exclamation-circle')} />
              <b className="ms-2">[{startLineNumber}:{startColumn} - {endLineNumber}:{endColumn}]</b>
              <span className="ms-2">{message}</span>
            </Alert>
          ))}

          {errors?.map((err, index) => (
            <Alert className="py-2 mb-2" key={index} variant="danger">
              <FontAwesomeIcon icon={solid('exclamation-circle')} />
              <b className="ms-2">{err.key}</b>
              <span className="ms-2">{err.message}</span>
            </Alert>
          ))}

          {(!definitionErrors || definitionErrors.length === 0) && (!errors || errors.length === 0) && (
            <Alert variant="success" className="py-2">
              <FontAwesomeIcon icon={solid('check-circle')} />
              <span className="ms-3">Definition looks good.</span>
            </Alert>
          )}
        </Col>
      </Row>
    )
  }

  const renderAsyncContent = () => {
    return service && frameworks
      ? renderServiceForm()
      : (
        <div className="p-5 text-center">
          <AppLoader size={350} />
        </div>
      );
  }

  return (
    <Container className="mt-3" fluid>
      <Helmet>
        <title>Service {id ? `${id} Edit` : "Registration"}</title>
      </Helmet>

      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/services' }}>Services</Breadcrumb.Item>
        {id
          ? (
            <>
              <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/services/${id}` }}>{service?.name}</Breadcrumb.Item>
              <Breadcrumb.Item active>Service Edit</Breadcrumb.Item>
            </>
          )
          : <Breadcrumb.Item active>New Service</Breadcrumb.Item>
        }
      </Breadcrumb>

      <Card className="mb-3">
        <Flex align="center" className="border-bottom p-2 px-3">
          <Col>
            <h5 className="mb-1">{id ? 'Service Edit' : 'New Service'}</h5>
            <h6 className="text-muted mb-0">{id ? 'Edit existing service.' : 'Register new service below.'}</h6>
          </Col>

          <Button size="sm" variant="light" className="w-auto border" onClick={openDefinitionModal}>
            <FontAwesomeIcon icon={solid('code')} />
            <span className="ms-2">Service Definition</span>
          </Button>
        </Flex>

        {renderAsyncContent()}
      </Card>

      <Modal
        title="Property Default Value"
        open={showDefaultValueModal}
        onClose={handleDefaultValueModalClose}
        onCancel={handleDefaultValueModalClose}
        footer={
          <Flex justify="center">
            <Button variant="light" size="sm" className="border" onClick={handleDefaultValueModalClose}>Cancel</Button>
          </Flex>
        }
      >
        <div className="mt-3">
          <ScriptEditor value={defaultValue} readOnly />
        </div>
      </Modal>

      <ServicePropertyModal
        open={showPropertyModal}
        onClose={handlePropertyModalClose}
        onSubmit={handleSubmitProperty}
        property={newProperty}
      />

      <Modal
        open={showDefinitionModal}
        onClose={handleDefinitionModalClose}
        onCancel={handleDefinitionModalClose}
        title="Service Definition"
        width={1000}
        footer={
          <Flex align="center" justify="space-between">
            <Button size="sm" variant="light" className="border" onClick={handleDefinitionModalClose}>Cancel</Button>
            <Button size="sm" variant="success" disabled={errors.length > 0 || definitionErrors.length > 0} onClick={submitDefinition}>Confirm</Button>
          </Flex>
        }
      >
        {renderServiceJSON()}
      </Modal>
    </Container>
  )
}
